import { catchError, map, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { DualPanelItem } from '../../../interfaces/DualPanelItem.interface';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../environment/environment';

export function dual_panel_subtypes(componentDataSourceService: ComponentDataSourceService) {
    return {
        name: 'Dual Panel Subtypes',
        description: 'Lists all subtypes in a format suitable for dual panel selector',
        execute: (params?: LoadDataParams) => {

            if (!params) {
                params = { dataSourceKey: 'dual-panel-subtypes', queryParams: {} };
            }
            if (!params.queryParams) {
                params.queryParams = {};
            }
            
            // Check if this is a request for specific IDs (for selected items)
            const hasIdsInQueryParams = params.queryParams['ids'] !== undefined;
            const hasIdsInFilters = params.filters && params.filters.some(f => f.column === 'ids');
            const hasIds = hasIdsInQueryParams || hasIdsInFilters;
            
            // Special checks for explicit by-ids flags
            const forceByIds = params.queryParams['forceByIds'] === true || 
                              params.queryParams['useByIdsEndpoint'] === true ||
                              params.queryParams['_byIds'] === 'true';
            
            const specialPath = (params as any).specialPath === 'by-ids';
            
            
            if (hasIds || forceByIds || specialPath) {
                
                // Extract the IDs from either queryParams or filters
                let selectedIds;
                if (params.queryParams['ids']) {
                    // If ids is a string, split it into an array
                    selectedIds = typeof params.queryParams['ids'] === 'string' 
                        ? params.queryParams['ids'].split(',').map(id => id.trim()) 
                        : params.queryParams['ids'];
                } else if (params.filters) {
                    // Find the ids filter
                    const idsFilter = params.filters.find(f => f.column === 'ids');
                    if (idsFilter && idsFilter.value) {
                        selectedIds = idsFilter.value;
                    }
                }
                
                // If we have IDs, make sure they're included in the API request
                if (selectedIds && selectedIds.length > 0) {
                    
                    // For clarity, remove pagination parameters when fetching by IDs
                    if (params.queryParams['pageSize']) {
                        delete params.queryParams['pageSize'];
                    }
                    if (params.queryParams['pageIndex']) {
                        delete params.queryParams['pageIndex'];
                    }
                    
                    // Add the IDs to the request
                    params.queryParams['ids'] = Array.isArray(selectedIds) 
                        ? selectedIds.join(',') 
                        : selectedIds;
                    
                    // Set a flag to indicate this is a special request for selected IDs
                    params.queryParams['includeSelectedIds'] = true;
                    
                    
                    // Use the by-ids endpoint explicitly
                    const byIdsUrl = `${environment.apiUrl}/events/subtypes/by-ids`;
                    
                    // Make the request to the by-ids endpoint
                    return componentDataSourceService.http.get<any>(
                        byIdsUrl,
                        { params: params.queryParams }
                    ).pipe(
                        catchError(error => {
                            console.error('[Subtypes] Error in by-ids endpoint:', error);
                            
                            // If the by-ids endpoint fails, fall back to the regular endpoint
                            const regularUrl = `${environment.apiUrl}/events/subtypes`;
                            
                            return componentDataSourceService.http.get<any>(
                                regularUrl,
                                { params: params.queryParams }
                            ).pipe(
                                catchError(fallbackError => {
                                    console.error('[Subtypes] Error in fallback fetch:', fallbackError);
                                    return of({ data: [] });
                                })
                            );
                        }),
                        map(result => {
                            if (result && result.items) {
                                try {
                                    // Map the subtypes to DualPanelItem format
                                    const items: DualPanelItem[] = result.items.map((subtype: any) => ({
                                        value: subtype.subtype_id,
                                        name: subtype.name,
                                        type: subtype.visibility // Using visibility as type
                                    }));
                                    
                                    return { data: items };
                                } catch (error) {
                                    console.error('[Subtypes] Error processing subtype data:', error);
                                    return { data: [] };
                                }
                            } else {
                                console.warn('[Subtypes] No specific subtypes found for the requested IDs');
                                return { data: [] };
                            }
                        })
                    );
                }
            }
            
            // For regular requests (no specific IDs), use standard pagination
            params.queryParams['pageSize'] = params.queryParams['pageSize'] || -1;
            
            return componentDataSourceService.http.get<any>(
                `${environment.apiUrl}/events/subtypes`,
                params?.queryParams ? { params: params.queryParams } : {}
            ).pipe(
                catchError(error => {
                    console.error('[Subtypes] Error fetching subtypes for dual panel:', error);
                    return of({ data: [] });
                }),
                map(result => {
                    if (result && result.items) {
                        try {
                            // Map the subtypes to DualPanelItem format
                            const items: DualPanelItem[] = result.items.map((subtype: any) => ({
                                value: subtype.subtype_id,
                                name: subtype.name,
                                type: subtype.visibility // Using visibility as type
                            }));
                            
                            return {
                                data: items // Wrap in data property as expected by dual-panel-selector
                            };
                        } catch (error) {
                            console.error('[Subtypes] Error converting subtypes to DualPanelItem format:', error);
                            return { data: [] };
                        }
                    } else {
                        console.warn('[Subtypes] No subtypes found');
                        return { data: [] };
                    }
                })
            );
        }
    };
} 